import { TenantDetails } from '@luminovo/api-contracts';
import { useHttpQuery } from '@luminovo/data-stack';
import { CenteredSpinner } from '@luminovo/design-system';
import React, { createContext, useContext, useEffect, useState } from 'react';

import { loadFromSessionStorage, saveToSessionStorage } from './tenantSessionCache';

interface TenantDetailsContextValue {
    tenantDetails: TenantDetails;
}

const defaultContextValue: TenantDetailsContextValue = {
    tenantDetails: {
        tenant: '',
        organization_name: 'Loading...',
        terms_and_conditions_link: '',
        is_self_signup_enabled: false,
        are_private_customers_allowed: false,
        organization_logo: null,
        colors: {
            primary_color: '#1976d2',
            primary_dark_color: '#115293',
            background_color: '#ffffff',
        },
        external_auth_org_id: null,
        login_theme: {
            primary_color: null,
            page_background_color: null,
            logo_url: null,
        },
    },
};

export const TenantDetailsContext = createContext<TenantDetailsContextValue>(defaultContextValue);

interface TenantDetailsProviderProps {
    children: React.ReactNode;
}

export const TenantDetailsProvider: React.FC<TenantDetailsProviderProps> = ({ children }) => {
    const cachedData = loadFromSessionStorage();
    const [tenantDetails, setTenantDetails] = useState<TenantDetails | null>(cachedData?.details || null);

    const { data, error } = useHttpQuery(
        'GET /tenant/details',
        { requestHeaders: { Authorization: null } },
        {
            enabled: true,
            staleTime: Infinity,
        },
    );

    useEffect(() => {
        if (data) {
            // oxlint-disable-next-line react/set-state-in-effect -- SOU-4697: the extra render pass is kept deliberately; deriving this during render or resetting via `key` would change what the user sees
            setTenantDetails(data);
            saveToSessionStorage(data);
        }
    }, [data]);

    if (!tenantDetails && error) {
        throw error;
    }

    if (!tenantDetails) {
        return <CenteredSpinner />;
    }

    // We have tenant details (either cached or fresh), render the app
    // Background refresh happens automatically on every page load
    return <TenantDetailsContext.Provider value={{ tenantDetails }}>{children}</TenantDetailsContext.Provider>;
};

export function useTenantDetailsContext(): TenantDetailsContextValue {
    return useContext(TenantDetailsContext);
}
